import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABEL = {
  sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
};

/**
 * Return the current day/time in Europe/London regardless of the visitor's timezone.
 * Essex businesses keep their hours in UK local time, so we normalise on the client.
 */
const getNowInLondon = () => {
  // `en-GB` with Europe/London gives us reliable HH:mm in 24h format.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'long',
  }).formatToParts(new Date());
  const weekday = parts.find(p => p.type === 'weekday')?.value?.toLowerCase();
  const hour = parts.find(p => p.type === 'hour')?.value;
  const minute = parts.find(p => p.type === 'minute')?.value;
  const minutes = (parseInt(hour, 10) * 60) + parseInt(minute, 10);
  return { weekday, minutes };
};

const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h * 60) + m;
};

const formatDuration = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/**
 * Compute open/closed status for a business given its `opening_hours` object.
 * Returns `null` if hours are missing or malformed.
 */
export const computeOpenStatus = (openingHours, now = getNowInLondon()) => {
  if (!openingHours || typeof openingHours !== 'object') return null;

  // Global Open 24/7 override — always show open, no time calculation needed
  if (openingHours.open_24_7 === true) {
    return { isOpen: true, label: 'Open 24/7', detail: 'Always open' };
  }

  // Only show a live status if the business has entered actual open/close times
  // for at least one day. If they've only marked days as closed (or left everything
  // blank), we have no data to compute a meaningful status — return null so the
  // badge is hidden rather than permanently showing "Closed".
  const hasAnyRealHours = Object.values(openingHours).some(
    entry => entry && typeof entry === 'object' && !entry.closed && entry.open && entry.close
  );
  if (!hasAnyRealHours) return null;

  const today = openingHours[now.weekday];
  if (!today) return null;

  // Currently open?
  if (!today.closed) {
    const open = toMinutes(today.open);
    const close = toMinutes(today.close);
    if (open != null && close != null) {
      // Same-day window (e.g. 09:00–17:00). For post-midnight close values (e.g. 02:00)
      // businesses typically keep their scheduled close on the CURRENT day's row, so we
      // interpret `close < open` as "closes tomorrow morning".
      const inWindow = close >= open
        ? now.minutes >= open && now.minutes < close
        : now.minutes >= open || now.minutes < close;
      if (inWindow) {
        const minsTillClose = close >= open
          ? close - now.minutes
          : (close > now.minutes ? close - now.minutes : (1440 - now.minutes) + close);
        return {
          isOpen: true,
          label: 'Open now',
          detail: minsTillClose <= 60
            ? `Closes in ${formatDuration(minsTillClose)}`
            : `Closes at ${today.close}`,
        };
      }
      // Not yet open today
      if (now.minutes < open) {
        const minsTillOpen = open - now.minutes;
        return {
          isOpen: false,
          label: 'Closed',
          detail: minsTillOpen <= 120
            ? `Opens in ${formatDuration(minsTillOpen)}`
            : `Opens today at ${today.open}`,
        };
      }
    }
  }

  // Closed today — find the next day that has hours set
  const todayIdx = DAY_ORDER.indexOf(now.weekday);
  for (let offset = 1; offset <= 7; offset++) {
    const d = DAY_ORDER[(todayIdx + offset) % 7];
    const entry = openingHours[d];
    if (!entry || entry.closed) continue;
    const open = toMinutes(entry.open);
    if (open == null) continue;
    return {
      isOpen: false,
      label: 'Closed',
      detail: offset === 1
        ? `Opens tomorrow at ${entry.open}`
        : `Opens ${DAY_LABEL[d]} at ${entry.open}`,
    };
  }

  return { isOpen: false, label: 'Closed', detail: null };
};

/**
 * Live status pill for a listing's opening hours.
 * Re-computes every 60s so the badge stays accurate without a page reload.
 */
const OpeningHoursStatus = ({ openingHours, variant = 'pill', className = '' }) => {
  const [status, setStatus] = useState(() => computeOpenStatus(openingHours));

  useEffect(() => {
    setStatus(computeOpenStatus(openingHours));
    const id = setInterval(() => setStatus(computeOpenStatus(openingHours)), 60_000);
    return () => clearInterval(id);
  }, [openingHours]);

  if (!status) return null;

  const colour = status.isOpen
    ? (status.label === 'Open 24/7' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-green-100 text-green-800 border-green-200')
    : 'bg-slate-100 text-slate-700 border-slate-200';
  const dot = status.isOpen ? (status.label === 'Open 24/7' ? 'bg-emerald-500' : 'bg-green-500') : 'bg-slate-400';

  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-1.5 text-sm ${status.isOpen ? 'text-green-700' : 'text-slate-600'} ${className}`} data-testid="opening-status">
        <span className={`h-2 w-2 rounded-full ${dot} ${status.isOpen ? 'animate-pulse' : ''}`} aria-hidden="true" />
        <span className="font-medium">{status.label}</span>
        {status.detail && <span className="text-slate-500">· {status.detail}</span>}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${colour} ${className}`}
      data-testid="opening-status"
      title={status.detail || ''}
    >
      <span className={`h-2 w-2 rounded-full ${dot} ${status.isOpen ? 'animate-pulse' : ''}`} aria-hidden="true" />
      {status.label}
      {status.detail && <span className="font-normal opacity-80">· {status.detail}</span>}
    </span>
  );
};

export default OpeningHoursStatus;
